/* eslint-disable indent */
import React, { FC, useState } from 'react';
import { Button, Paper, Typography } from '@mui/material';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
	IQuizQuestion,
	IQuizQuestionTypes,
} from '../../../lib/interfaces/Quiz';
import ContentRenderer from '../Content/ContentRenderer';
import './QuizQuestion.scss';

type QuizQuestionProps = {
	question: IQuizQuestion;
	questionIndex: number;
	isCurrent: boolean;
	checkMistakesMode: boolean;
	onPrev: () => void;
	onNext: (answer: string | string[], questionId: string) => void;
};

const QuizQuestion: FC<QuizQuestionProps> = ({
	question,
	isCurrent,
	questionIndex,
	onPrev,
	onNext,
	checkMistakesMode,
}) => {
	const { t } = useTranslation();
	const [exactAnswer, setExactAnswer] = useState<string>('');
	const [optionAnswer, setOptionAnswer] = useState<string[]>([]);

	const onSave = () => {
		if (question.type === IQuizQuestionTypes.EXACT_ANSWER) {
			onNext(exactAnswer, question.id);
		} else {
			onNext(optionAnswer, question.id);
		}
	};

	const checkAnswer = (id: string) => {
		if (!checkMistakesMode) {
			if (question.type === IQuizQuestionTypes.OPTIONS) {
				setOptionAnswer([id]);
			} else if (question.type === IQuizQuestionTypes.MULTIPLE_OPTIONS) {
				const newAnswers = [...optionAnswer];
				if (optionAnswer.includes(id)) {
					newAnswers.splice(newAnswers.indexOf(id), 1);
				} else {
					newAnswers.push(id);
				}
				setOptionAnswer(newAnswers);
			}
		}
	};

	const getOptionClass = (optionId: string) =>
		classNames('question-wrapper', {
			'selected-answer': optionAnswer.includes(optionId),
			'wrong-selected-answer':
				checkMistakesMode &&
				optionAnswer.includes(optionId) &&
				!question.answer?.includes(optionId),
			'right-selected-answer':
				checkMistakesMode &&
				optionAnswer.includes(optionId) &&
				question.answer?.includes(optionId),
		});

	return (
		<div style={{ height: isCurrent ? 'auto' : '0', overflow: 'hidden' }}>
			<div
				style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}
			>
				<div style={{ paddingRight: '5px', paddingTop: '14px', fontWeight: 600 }}>
					{questionIndex + 1}.
				</div>{' '}
				<ContentRenderer className="question-title" html={question.text} />
			</div>
			<div style={{ display: 'flex', flexWrap: 'wrap' }}>
				{(question.type === IQuizQuestionTypes.OPTIONS ||
					question.type === IQuizQuestionTypes.MULTIPLE_OPTIONS) &&
					question.options?.map((option, index) => (
						<Paper
							onClick={() => checkAnswer(option.id)}
							key={option.id}
							elevation={0}
							variant="outlined"
							// className={`question-wrapper ${
							// optionAnswer.includes(option.id) ? 'selected-answer' : ''
							// }`}
							className={getOptionClass(option.id)}
						>
							<div style={{ display: 'flex', alignItems: 'flex-start' }}>
								<div
									style={{ padding: 14, backgroundColor: 'rgba(171, 171, 171, 0.15)' }}
								>
									<Typography>{index + 1}. </Typography>
								</div>
								<div style={{ padding: 10 }}>
									<ContentRenderer html={option.text} />
								</div>
							</div>
							{checkMistakesMode && optionAnswer.includes(option.id) && (
								<div
									style={{ display: 'flex', alignItems: 'center', paddingRight: '10px' }}
								>
									{question.answer?.includes(option.id) ? (
										<CheckCircleIcon color="success" />
									) : (
										<CancelIcon color="error" />
									)}
								</div>
							)}
						</Paper>
					))}
			</div>
			{checkMistakesMode &&
				(question.type === IQuizQuestionTypes.MULTIPLE_OPTIONS ||
					question.type === IQuizQuestionTypes.OPTIONS) &&
				optionAnswer.length < (question.answer?.length || 0) && (
					<Typography variant="body1" color="error">
						{t('moreOptionsNeeded')}
					</Typography>
				)}
			{question.type === IQuizQuestionTypes.EXACT_ANSWER && (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<TextField
						onChange={(
							e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
						) => {
							setExactAnswer(e.target.value);
						}}
						id="outlined-basic"
						label={t('yourAnswer')}
						variant="outlined"
						disabled={checkMistakesMode}
					/>
					{checkMistakesMode && (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							{question.answer?.includes(exactAnswer) ? (
								<CheckCircleIcon color="success" />
							) : (
								<CancelIcon color="error" />
							)}
						</div>
					)}
				</div>
			)}
			<div style={{ marginTop: '10px' }}>
				{questionIndex !== 0 && (
					<Button
						onClick={onPrev}
						startIcon={<ChevronLeftIcon />}
						style={{ marginRight: '20px' }}
						variant="outlined"
						color="secondary"
					>
						{t('prev')}
					</Button>
				)}
				<Button
					onClick={onSave}
					endIcon={<ChevronRightIcon />}
					variant="outlined"
					color="secondary"
				>
					{t('next')}
				</Button>
			</div>
		</div>
	);
};

export default QuizQuestion;
